import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Modal } from 'react-bootstrap';

import applicationRouter from '~/hoc/applicationRouter';
import withLocalization from '~/hoc/withLocalization';
import withRemoveDialog from '~/hoc/withRemoveDialog';
import Button from '../../../components/CustomButton/CustomButton.jsx';
import { Input } from '../../../elements/Input';
import GenericList from '../../../components/GenericList/GenericList.js';
import RequiredStar from '../../../elements/RequiredStar';

import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg';
import deleteIcon from '../../../assets/img/deleting.svg';
import editIcon from '../../../assets/img/editing.svg';
import hygieneZoneIcon from '../../../assets/img/hygiene-zone.svg';
import impureZoneIcon from '../../../assets/img/impure-zone.svg';
import '../../../styles/css/styles.css';

const ZONE_ICONS = {
    general: <img src={impureZoneIcon} alt="impure zone" width={36} height={36} />,
    hygiene: <img src={hygieneZoneIcon} alt="hygiene zone" width={36} height={36} />,
    restricted: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
        </svg>
    ),
    outdoor: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 8C8 10 5.9 16.17 3.82 22"/>
            <path d="M9.5 5.5a7 7 0 0 1 8.5 8.5"/>
            <path d="M12 2a10 10 0 0 1 8 16"/>
        </svg>
    ),
};

class VisitorZones extends Component {
    constructor(props) {
        super(props);
        this.state = {
            showModal: false,
            editingZone: null,
            zoneName: '',
            zoneDesc: '',
            zoneReq: '',
            filters: {},
        };
    }

    requestData = () => {
        return this.props.visitorStore.loadZones().then(data => ({
            rows: data.zones || [],
            pages: 1,
            time: data.time,
        }));
    };

    openEdit = (zone) => {
        this.setState({
            showModal: true,
            editingZone: zone,
            zoneName: zone.name,
            zoneDesc: zone.description || '',
            zoneReq: zone.requirements || '',
        });
    };

    closeModal = () => {
        this.setState({ showModal: false });
    };

    handleSave = async () => {
        const { visitorStore, commonStore, t } = this.props;
        const { editingZone, zoneName, zoneDesc, zoneReq } = this.state;

        if (!zoneName.trim()) {
            commonStore.addNotification(t('Please enter zone name'), null, 'error');
            return;
        }

        try {
            await visitorStore.saveZone({
                id: editingZone ? editingZone.id : undefined,
                name: zoneName,
                description: zoneDesc,
                requirements: zoneReq,
                zone_type: editingZone ? editingZone.zone_type : 'general',
            });
            commonStore.addNotification(t('Zone Saved'), null, 'success');
            this.setState({ showModal: false });
        } catch (e) {
            commonStore.addNotification(t(e.message || 'Error saving zone'), null, 'error');
        }
    };

    handleDelete = (id) => {
        const { commonStore, t } = this.props;
        return this.props.handleDelete(id, async (id) => {
            await this.props.visitorStore.deleteZone(id);
            commonStore.addNotification(t('Zone Deleted'), null, 'error');
        });
    };

    getZoneIcon = (type) => ZONE_ICONS[type] || ZONE_ICONS.general;

    renderRequirementsBadge(zone) {
        if (!zone.requirements) return null;
        const isHygiene = zone.zone_type === 'hygiene';
        return (
            <div className={`d-flex align-items-center ${isHygiene ? 'text-success' : 'text-warning'}`}
                style={{ fontSize: 12 }}>
                {isHygiene ? (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                ) : (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                        <line x1="12" y1="9" x2="12" y2="13"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                    </svg>
                )}
                {zone.requirements}
            </div>
        );
    }

    getColumns() {
        const { t } = this.props;
        return [
            {
                Header: '',
                id: 'icon',
                accessor: row => row.zone_type,
                isSortable: false,
                width: 60,
                Cell: ({ row }) => this.getZoneIcon(row.original.zone_type),
            },
            {
                Header: 'Zone',
                accessor: 'name',
                isSortable: false,
                Cell: ({ row }) => <span className="font-weight-bold">{row.original.name}</span>,
            },
            {
                Header: 'Description',
                accessor: 'description',
                isSortable: false,
                Cell: ({ row }) => <span className="text-muted">{row.original.description || '—'}</span>,
            },
            {
                Header: 'Requirements',
                id: 'requirements',
                accessor: row => row.requirements || '',
                isSortable: false,
                Cell: ({ row }) => this.renderRequirementsBadge(row.original),
            },
            {
                Header: 'Actions',
                id: 'actions',
                accessor: row => row.id,
                isSortable: false,
                Cell: ({ row }) => (
                    <div className="actions-center justify-content-center">
                        <Button icon_sm fill onClick={() => this.openEdit(row.original)}>
                            <img src={editIcon} alt="edit" />
                        </Button>
                        {!row.original.is_default && (
                            <Button icon_sm_delete fill onClick={() => this.handleDelete(row.original.id)}>
                                <img src={deleteIcon} alt="delete" />
                            </Button>
                        )}
                    </div>
                ),
            },
        ];
    }

    render() {
        const { t, visitorStore } = this.props;
        const { showModal, zoneName, zoneDesc, zoneReq, filters } = this.state;

        return (
            <div className="table-list">
                <GenericList
                    columns={this.getColumns()}
                    requestData={this.requestData}
                    filters={filters}
                    lastListLoadTime={visitorStore.zonesLastLoadTime}
                    tableHeader={t('Zone Configuration')}
                />

                <Modal show={showModal} onHide={this.closeModal}>
                    <Modal.Header className="set_ModalHeader">
                        <Modal.Title className="w-100 text-center custom-modal-title">
                            {t('Edit Zone')}
                        </Modal.Title>
                        <img src={CloseModalIcon} alt="close" className="cursor-pointer" onClick={this.closeModal} />
                    </Modal.Header>
                    <Modal.Body>
                        <div className="row">
                            <div className="col-12">
                                <div className="form-group field field-string w-100">
                                    <label className="control-label fs15">{t('Zone Name')}<RequiredStar /></label>
                                    <Input
                                        value={zoneName}
                                        onChange={e => this.setState({ zoneName: e.target.value })}
                                        placeholder={t('e.g. Clean Zone')}
                                        maxLength={100}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <div className="form-group field field-string w-100">
                                    <label className="control-label fs15">{t('Description')}</label>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        maxLength={500}
                                        value={zoneDesc}
                                        onChange={e => this.setState({ zoneDesc: e.target.value })}
                                        placeholder={t('e.g. Production floor, Packaging lines, Labs')}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-12">
                                <div className="form-group field field-string w-100">
                                    <label className="control-label fs15">{t('Requirements / Warning')}</label>
                                    <Input
                                        value={zoneReq}
                                        onChange={e => this.setState({ zoneReq: e.target.value })}
                                        placeholder={t('e.g. Minimal PPE required')}
                                        maxLength={100}
                                    />
                                    <p className="text-muted mb-0">
                                        {t('Shown as a warning to visitors on the registration screen.')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={this.handleSave} className="btn-wd btn-fill">
                            {t('Save Zone')}
                        </Button>
                        <Button variant="secondary" onClick={this.closeModal} className="btn-wd">
                            {t('Cancel')}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        );
    }
}

export default inject('visitorStore', 'userStore', 'commonStore')(
    applicationRouter(withLocalization(withRemoveDialog(observer(VisitorZones))))
);
